/*
 * Copyright (C) 2019-2022 Corvalent Corporation - All Rights Reserved
 * This software is licensed program property and is Proprietary and Confidential.
 * Unauthorized copying of this file via any medium is strictly prohibited unless
 * prior written permission has been obtained by Corvalent Corporation.
 * In addition, the look and feel of this software as well as the user interface
 * design is also Copyright ©.
 * Notice: This software is not open source.
 * This software is subject to the Corvalent Software License Agreement.
 * You should have received a copy of this license with the software.
 * If not, please write to: sales@corvalent.com
 * All information contained herein is, and remains, the property of
 * Corvalent Corporation and its suppliers, if any. This software may also
 * be covered by U.S. and Foreign Patents, patents in progress, and are
 * protected by trade secret or copyright law
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  AppBar,
  Box,
  IconButton,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { Logout, Menu } from '@mui/icons-material';

import { logout } from '../../axios/axiosAuth';
import { useGeneralContext } from '../../context/GeneralContext';
import LanguageSelect from './LanguageSelect';
import * as S from './style';
import NotificationContainer from '../NotificationContainer';
import {
  closeAnimated,
  openAnimated,
} from '../../redux/actions/sideDrawerActions';
import CustomButton from '../shared/atoms/CustomButton';
import { useConfirmation } from '../../context/ConfirmationContext';
import CenterTitle from './CenterTitle';
import SensorConnection from './SensorConnection';
import NotificationConnection from './NotificationConnection';
import { FALLBACK_INITIAL_POSITION, strToGeo } from '../../utils/geolocation';

const Navbar = () => {
  const history = useHistory();
  const { user, resetUser, sideMenuForm, setSideMenuForm, customization } =
    useGeneralContext();
  const { openConfirmation } = useConfirmation();
  const dispatch = useDispatch();
  const openAnimatedDispatch = args => dispatch(openAnimated(args));
  const closeAnimatedDispatch = args => dispatch(closeAnimated(args));
  const drawerIsOpen = useSelector(store => store.sideDrawerState.isOpen);
  const [notification, setNotification] = useState(false);
  const { t } = useTranslation();

  const goToStart = () => {
    const push = () => {
      const { longitude, latitude, zoom } =
        strToGeo(
          customization.initial_position ||
            process.env.REACT_APP_MAP_INITIAL_POSITION,
        ) || FALLBACK_INITIAL_POSITION;

      if (history.location.pathname.includes('/portal/maps') && window.map) {
        window.map.flyTo({
          center: [longitude, latitude],
          zoom,
        });
      }

      history.push({
        pathname: '/portal/maps',
      });
    };
    if (drawerIsOpen && sideMenuForm) {
      openConfirmation({
        message: t('modal.discard.description'),
        handleAction: () => {
          push();
          setSideMenuForm(false);
        },
        title: t('modal.cancel'),
      });
    } else {
      push();
    }
  };

  const logoutAction = () => {
    openConfirmation({
      message: t('tools_menu.logout_confirmation'),
      handleAction: () => logout(resetUser),
      title: t('tools_menu.logout'),
      actionLabel: t('tools_menu.logout'),
    });
  };

  const getUserByEmail = email =>
    email?.indexOf('@') ? email.slice(0, email.indexOf('@')) : email;

  const switchSidebar = () => {
    if (drawerIsOpen && sideMenuForm) {
      openConfirmation({
        message: t('modal.discard.description'),
        handleAction: closeAnimatedDispatch,
        title: t('modal.cancel'),
      });
    } else {
      drawerIsOpen ? closeAnimatedDispatch() : openAnimatedDispatch();
    }
  };

  const switchNotification = () => setNotification(prev => !prev);

  // Fuction to get the target for clickOutside wrapper in the NotificationContainer
  // Clickoutide currently disabled.
  const clickOutsideNotification = e => {
    if (!e.target.className.includes('toggle-notification'))
      setNotification(false);
  };

  return (
    <AppBar
      position="fixed"
      sx={theme => ({
        zIndex: theme.zIndex.drawer + 1,
        borderBottom: `1px solid ${theme.palette.primary.main}`,
        color: 'white',
      })}
      className="cv-bg-secondary"
      id="navbar"
    >
      <Toolbar
        variant="dense"
        sx={{ maxHeight: '3.125rem', height: '3.125rem' }}
      >
        <Stack
          direction="row"
          alignItems="center"
          width="100%"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center">
            <IconButton
              id="sandwich-button"
              edge="start"
              color="inherit"
              aria-label="open drawer"
              onClick={switchSidebar}
              aria-expanded={drawerIsOpen}
            >
              <Menu id="sandwich-button" />
            </IconButton>
            {customization?.navbar_logo && (
              <S.img
                src={customization.navbar_logo}
                alt="platform logo"
                onClick={goToStart}
              />
            )}
          </Stack>
          <CenterTitle />
          <S.UserInfo>
            <SensorConnection />
            <LanguageSelect />
            <Typography sx={{ ml: 1 }} display={{ xs: 'none', sm: 'block' }}>
              {getUserByEmail(user?.email)}
            </Typography>
            <NotificationConnection
              switchNotification={switchNotification}
              containerState={notification}
            />
            <NotificationContainer
              containerState={notification}
              onClickOutside={clickOutsideNotification}
              onClickOutsideMobileOnly
              switchNotification={switchNotification}
            />
            <CustomButton style={{ height: '30px' }} onClick={logoutAction}>
              <Logout sx={{ fontSize: { xs: 20, sm: 24 } }} />{' '}
              <Box display={{ xs: 'none', md: 'block' }}>
                {t('tools_menu.logout')}
              </Box>
            </CustomButton>
          </S.UserInfo>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
